/**
 * Visualizer payload builder — shared between DevToolsOverlay and ResultsVisualCard.
 *
 * Produces the exact shape the legacy PHP endpoint (/api/visualizer.php)
 * expects. Extracting this keeps the dev-panel button and the user-facing
 * card in lock-step so the rendered image matches the saved configuration
 * regardless of which caller triggered the request.
 */

import type { WizardState, EstimateOutput } from '../../types';
import { SPACE_TYPES } from '../../data/presets';

/** The visualizer's slug for each space type (data/presets.ts SPACE_TYPES `legacy`). */
export const SPACE_MAP: Record<string, string> = Object.fromEntries(SPACE_TYPES.map((t) => [t.id, t.legacy]));

export function slatWallTier(numRows: number, wallHeight: number): '25' | '50' | null {
  if (numRows <= 0 || wallHeight <= 0) return null;
  const pct = ((numRows * 0.5) / wallHeight) * 100;
  if (pct < 1)  return null;
  if (pct < 38) return '25';
  return '50';
}

/**
 * Build the JSON payload the PHP endpoint expects.
 *
 * @param wizardState   Live session state or SavedEstimate.wizardState. When
 *                      null, falls back to a generic rendering.
 * @param estimateResult The calculated estimate (for roomDimensions.height).
 * @param overrideSpace  Optional: dropdown-selected space id that bypasses
 *                      the SPACE_MAP lookup. Pass undefined to use the
 *                      user's saved spaceType (always the initial render).
 */
export function buildVisualizerPayload(
  wizardState: WizardState | null,
  estimateResult: EstimateOutput,
  overrideSpace?: string,
): Record<string, unknown> {
  // Defensive fallback: if wizardState is unavailable, render a neutral space
  // using the dimensions we know from the estimate itself.
  if (!wizardState) {
    return {
      wall: '1',
      space: overrideSpace ?? 'generic',
      color: 'white',
      orientation: 'horizontal',
      ceiling: false,
      slatwall: false,
    };
  }

  const isCeilingOnly = wizardState.calculationMode === 'CeilingOnly';
  const isIndividualWalls = wizardState.calculationMode === 'IndividualWalls';
  const mappedSpace = SPACE_MAP[wizardState.applicationConfig.spaceType] || 'generic';
  const space = overrideSpace ?? mappedSpace;

  // In IndividualWalls mode the wall-level color / orientation / slatwall
  // data lives on each wall, not on wallsConfig (which still holds FullRoom
  // defaults). The bitmap can only show ONE wall, so we render the FIRST
  // enabled wall's look — matches how the data-point list summarizes that
  // mode too.
  const firstIndividualWall = isIndividualWalls
    ? (wizardState.individualWalls?.find(w => w.enabled) ?? null)
    : null;

  const wallColor = firstIndividualWall
    ? firstIndividualWall.color
    : wizardState.wallsConfig.color;
  const wallOrientation = firstIndividualWall
    ? firstIndividualWall.orientation
    : wizardState.wallsConfig.orientation;

  // SlatWall height reference:
  //   - IndividualWalls: use the first wall's own height (per-wall heights vary)
  //   - Otherwise: use the estimate's room height
  const wallHeight = firstIndividualWall
    ? firstIndividualWall.height
    : estimateResult.input.roomDimensions.height;

  // SlatWall tier + color:
  //   - CeilingOnly: no slatwall
  //   - IndividualWalls: pull from the first wall's slatWall.* only if enabled
  //   - FullRoom: pull from wallsConfig.slatWallNumPanels etc.
  let tier: '25' | '50' | null = null;
  let slatWallColorForPayload: string = wizardState.wallsConfig.slatWallColor;
  if (!isCeilingOnly) {
    if (firstIndividualWall) {
      if (firstIndividualWall.slatWall.enabled) {
        tier = slatWallTier(firstIndividualWall.slatWall.numPanelRows, wallHeight);
        slatWallColorForPayload = firstIndividualWall.slatWall.color;
      }
    } else {
      tier = slatWallTier(wizardState.wallsConfig.slatWallNumPanels, wallHeight);
    }
  }

  // CeilingOnly mode has no wall UI, so colors come from the first ceiling zone
  // and we always paint the ceiling layer.
  const firstZoneColor = wizardState.ceilingConfig.zones?.[0]?.color;
  const ceilingOn = isCeilingOnly ? true : wizardState.ceilingConfig.include;
  const ceilingColor = isCeilingOnly
    ? (firstZoneColor ?? wizardState.ceilingConfig.color)
    : wizardState.ceilingConfig.color;

  const payload: Record<string, unknown> = {
    wall: '1',
    space,
    color: wallColor.toLowerCase(),
    orientation: wallOrientation.toLowerCase(),
    ceiling: ceilingOn,
    slatwall: tier !== null,
  };
  if (ceilingOn) {
    payload.ceilingcolor = String(ceilingColor).toLowerCase();
  }
  if (tier !== null) {
    payload.slatwallcoverage = tier;
    payload.slatwallcolor = slatWallColorForPayload.toLowerCase();
  }

  return payload;
}

/**
 * Build the full /api/visualizer.php URL for a given payload.
 */
export function buildVisualizerUrl(
  payload: Record<string, unknown>,
  width?: number,
  height?: number,
): string {
  const params = new URLSearchParams();
  params.set('jsonInput', JSON.stringify(payload));
  if (width)  params.set('width', String(width));
  if (height) params.set('height', String(height));
  return `/api/visualizer.php?${params.toString()}`;
}
